import { convertToLb, roundValue } from "./coachingHelpers";

// Metrics that get a monthly-average column, mapped to how one logged day
// turns into a number and how the average is shown.
const AVERAGE_METRICS = {
  bodyWeight: {
    toNumber: (value, { unitSelected }) =>
      unitSelected === "metric" ? value : convertToLb(value),
    unit: (unitSelected) => (unitSelected === "metric" ? "kg" : "lb"),
  },
  nutrients: {
    toNumber: ({ proteins = 0, carbs = 0, fats = 0 } = {}) =>
      proteins * 4 + carbs * 4 + fats * 9,
    format: (avg) => String(Math.round(avg)),
    unit: () => "kcal",
  },
  readiness: {
    toNumber: (value) => Object.values(value || {}).reduce((a, b) => a + b, 0),
  },
  hrv: { toNumber: (value) => value, unit: () => "ms" },
  restingHeartRate: { toNumber: (value) => value, unit: () => "bpm" },
  bodyFatPercentage: { toNumber: (value) => value, unit: () => "%" },
  sleep: {
    toNumber: (ms) => ms / 3.6e6,
    unit: () => "h",
  },
  waterIntake: {
    toNumber: (liters) => liters,
    unit: () => "L",
  },
  sessionRPE: { toNumber: (value) => value },
  steps: {
    toNumber: (value) => value,
    format: (avg) => Math.round(avg).toLocaleString("en-US"),
    unitKey: "steps",
  },
};

// Metrics that get a 30-day total column: the API sends the count as
// `total30`, and the key names the pluralised unit shown next to it.
const TOTAL_METRICS = {
  videos: "videos",
  bodyMeasurements: "logs",
  bodyPhotos: "photos",
  mealPhotos: "photos",
  "coach-notes": "comments",
  "coach-checkin-review": "reviews",
  "coach-video-comment": "comments",
  "coach-photo-comment": "comments",
  "coach-video-feedback": "videos",
  "coach-chat": "messages",
};

export const hasSummaryColumn = (metric) =>
  !metric ||
  metric === "workouts" ||
  metric === "check-in" ||
  metric === "menstrualCycle" ||
  metric in TOTAL_METRICS ||
  metric in AVERAGE_METRICS;

/**
 * Returns the end-of-row summary, always over the last 30 days:
 *  - workouts / check-in: `{ type: "count", done, scheduled, missed? }`
 *  - totals: `{ type: "total", value, unitKey }`
 *  - menstrual cycle: `{ type: "cycleDay", value }` (value null if unknown)
 *  - averaged metrics: `{ type: "average", value, text, unit }`
 *    (value null if no logs)
 */
export const getMonthSummary = ({ athlete, metric, unitSelected }) => {
  if (!metric || metric === "workouts") {
    // Older API responses only carry the month grid.
    if (athlete.done30 !== undefined) {
      return {
        type: "count",
        done: athlete.done30,
        scheduled: athlete.scheduled30,
        missed: athlete.missed30 ?? 0,
      };
    }
    let done = 0;
    let scheduled = 0;
    athlete.scheduled.forEach((day) => {
      if (!day) return;
      scheduled++;
      if (day === "Complete") done++;
    });
    return { type: "count", done, scheduled, missed: athlete.missed30 ?? 0 };
  }

  if (metric === "check-in") {
    if (athlete.checkIns30) return { type: "count", ...athlete.checkIns30 };
    const scheduledDays = athlete.days.filter((day) => day?.status);
    return {
      type: "count",
      done: scheduledDays.filter((day) => day.status === "completed").length,
      scheduled: scheduledDays.length,
    };
  }

  if (metric === "menstrualCycle") {
    return { type: "cycleDay", value: athlete.cycleDay ?? null };
  }

  if (metric in TOTAL_METRICS) {
    return {
      type: "total",
      value: athlete.total30 ?? null,
      unitKey: TOTAL_METRICS[metric],
    };
  }

  const { toNumber, format, unit, unitKey } = AVERAGE_METRICS[metric];
  const numbers = (athlete.last30Values ?? [])
    .map((value) => toNumber(value, { unitSelected }))
    .filter((n) => Number.isFinite(n) && (metric === "sessionRPE" || n > 0));
  const unitText = unit?.(unitSelected) ?? "";
  if (numbers.length === 0) {
    return { type: "average", value: null, text: "", unit: unitText, unitKey };
  }
  const value = numbers.reduce((a, b) => a + b, 0) / numbers.length;
  return {
    type: "average",
    value,
    text: format ? format(value) : String(roundValue(value)),
    unit: unitText,
    unitKey,
    count: numbers.length,
  };
};

// Number the summary column sorts by; null (nothing to show) sorts last.
export const getSummarySortValue = ({ athlete, metric, unitSelected }) => {
  if (!hasSummaryColumn(metric)) return null;
  const summary = getMonthSummary({ athlete, metric, unitSelected });
  if (summary.type === "count") {
    return summary.scheduled ? summary.done / summary.scheduled : null;
  }
  return summary.value;
};
